import React, { useEffect } from "react";
import Pagination from "../reusable/Pagination";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { selectUserInformations } from "../../features/authSlice";
import { deleteMaterial, selectMaterialDeleteStatus, selectMaterialDeleteError } from "../../features/materials/materialSlice";
import { hasPermission, showToast } from "../../utils/utils";
import Loader from "../Live/Loader";
import ActionsDropdown from "../reusable/ActionsDropdown";
import { FiRefreshCw } from "react-icons/fi";
import { MdDeleteOutline, MdInsertDriveFile, MdImage, MdOutlinePictureAsPdf, MdDescription, MdTableChart, MdVideoLibrary, MdAudiotrack } from "react-icons/md";
import { API } from "../../shared/api/Api";
import { saveAs } from 'file-saver';
import axios from "axios";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import { ReactComponent as ExcelIcon } from '../../assets/svg/Excel.svg';

/**
 * MaterialTable Component
 * Refactored to use navigation instead of modals
 */
const MaterialTable = ({
  materials,
  currentPage,
  totalPages,
  onSearchChange,
  onPageChange,
  loader,
  setReFetchData,
  courseMaterials = false,
  courseData = null,
  onSort,
  currentOrderBy,
  currentOrderDir,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);
  const param = useParams();
  const deleteStatus = useSelector(selectMaterialDeleteStatus);
  const deleteError = useSelector(selectMaterialDeleteError);

  // Handle delete with confirmation
  const handleDelete = async (materialId) => {
    if (window.confirm("Are you sure you want to delete this material? There is no going back!!")) {
      const result = await dispatch(deleteMaterial({ token: user.token, materialId }));
      
      if (deleteMaterial.fulfilled.match(result)) {
        showToast('Success', 'Material deleted successfully', 'success');
        if (setReFetchData) {
          setReFetchData(true);
        }
      } else if (deleteMaterial.rejected.match(result)) {
        showToast('Error', result.payload || 'Failed to delete material', 'error');
      }
    }
  };

  // Handle delete errors
  useEffect(() => {
    if (deleteStatus === 'failed' && deleteError) {
      showToast('Error', deleteError, 'error');
    }
  }, [deleteStatus, deleteError]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const extractFileNameAndMime = (filePath) => {
    const parts = filePath.split("/");
    const fileName = parts[parts.length - 1];
    return { fileName };
  };

  const handleDownloadMaterials = async (id, path) => {
    try {
      const response = await axios.get(
        `${API.Materials.app_courses_materials_download}/${id}`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (response.status === 200) {
        const blob = new Blob([response.data]);
        const { fileName } = extractFileNameAndMime(path);
        saveAs(blob, fileName);
        if (setReFetchData) {
          setReFetchData(true);
        }
      }
    } catch (error) {
      showToast(
        "Oops, Something went wrong",
        "There is an error while downloading a file, try again later",
        "error"
      );
    }
  };

  const formatMimeType = (mimeType) => {
    const typeMap = {
      "application/pdf": "PDF",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word Document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel Spreadsheet",
      "application/msword": "Word Document",
      "application/vnd.ms-excel": "Excel Spreadsheet",
      "application/zip": "ZIP Archive",
      "text/csv": "CSV File",
      "image/jpeg": "JPEG Image",
      "image/png": "PNG Image",
      "video/mp4": "MP4 Video",
      "audio/mpeg": "MP3 Audio",
      "application/vnd.ms-powerpoint": "PowerPoint Presentation",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PowerPoint Presentation",
    };
    return typeMap[mimeType] || "File";
  };

  const FilePreviewCell = ({ item }) => {
    const formattedSize = formatFileSize(item.size);
    let Icon = MdInsertDriveFile;
    let iconColor = "text-gray-600";
    let fileType = "File";

    if (item.mime_type.startsWith("image/")) {
      Icon = MdImage;
      iconColor = "text-blue-600";
      fileType = "Image";
    } else if (item.mime_type === "application/pdf") {
      Icon = MdOutlinePictureAsPdf;
      iconColor = "text-red-600";
      fileType = "PDF";
    } else if (item.mime_type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
      Icon = MdDescription;
      iconColor = "text-blue-400";
      fileType = "Word Document";
    } else if (item.mime_type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
      Icon = MdTableChart;
      iconColor = "text-green-600";
      fileType = "Excel Spreadsheet";
    } else if (item.mime_type === "video/mp4") {
      Icon = MdVideoLibrary;
      iconColor = "text-purple-600";
      fileType = "Video";
    } else if (item.mime_type === "audio/mpeg") {
      Icon = MdAudiotrack;
      iconColor = "text-orange-600";
      fileType = "Audio";
    }

    return (
      <div className="flex items-center space-x-3">
        <a
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center rounded-lg bg-gray-50 hover:bg-gray-100 p-2 transition-colors"
          title={`Open ${fileType} in new tab`}
        >
          <Icon className={`${iconColor} w-8 h-8`} />
        </a>
        <div className="flex flex-col">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            {fileType}
          </span>
          <span className="text-sm text-gray-700">{formattedSize}</span>
          <button
            className="mt-1 inline-flex text-xs font-medium text-primary hover:text-primary-hover"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadMaterials(item.id, item.path);
            }}
          >
            Download
          </button>
        </div>
      </div>
    );
  };

  const columns = [
    { id: "course_title", label: "Course Title", sortable: false },
    { id: "title", label: "Title", sortable: true },
    { id: "description", label: "Description", sortable: true },
    { id: "media_type", label: "Media Type", sortable: true },
    { id: "mime_type", label: "Mime Type", sortable: true },
    { id: "file", label: "File", sortable: false },
    { id: "uploadedBy_username", label: "Uploaded By", sortable: false },
    { id: "created_at", label: "Created At", sortable: true },
  ];

  const handleSortClick = (columnId) => {
    if (currentOrderBy === columnId) {
      const newDirection = currentOrderDir === "ASC" ? "DESC" : "ASC";
      onSort(columnId, newDirection);
    } else {
      onSort(columnId, "ASC");
    }
  };

  const renderSortIndicator = (columnId) => {
    const isActive = currentOrderBy === columnId;
    return (
      <div className="inline-flex flex-col ml-1">
        <FaSortUp
          className={`${isActive && currentOrderDir === "ASC" ? "text-primary" : "text-gray-400"} -mb-1`}
          size={12}
        />
        <FaSortDown
          className={`${isActive && currentOrderDir === "DESC" ? "text-primary" : "text-gray-400"} -mt-1`}
          size={12}
        />
      </div>
    );
  };

  const exportToExcel = async () => {
    try {
      const response = await axios.get(API.Materials.app_courses_materials_export_to_excel, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      let filename;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('filename=') !== -1) {
        filename = disposition.split('filename=')[1].trim().replace(/['"]/g, '');
      } else {
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, '0');
        const formatted = `${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}`;
        filename = `materials_${formatted}.xlsx`;
      }
      saveAs(response.data, filename);
      showToast('Success', 'Materials exported successfully', 'success');
    } catch (error) {
      showToast(
        'Export Failed',
        error?.response?.data?.message || 'Could not export materials. Please try again.',
        'error'
      );
    }
  };

  const getDropdownActions = (id) => {
    const actions = [];

    if (hasPermission(user, "view_material")) {
      actions.push({
        icon: (
          <svg
            className="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ),
        label: "Preview",
        onClick: () => navigate(`/dashboard/materials/${id}`),
      });
    }

    if (hasPermission(user, "delete_material")) {
      actions.push({
        icon: <MdDeleteOutline className="w-4 h-4" />,
        label: "Delete",
        onClick: () => handleDelete(id),
      });
    }

    return actions;
  };

  return (
    <div className="w-full mx-auto mt-8 px-2 sm:px-4 lg:px-0">
      <div className="bg-white border border-gray-100 shadow-xl rounded-2xl p-4 sm:p-6 lg:p-8 relative">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-anek-odia text-2xl sm:text-3xl font-semibold text-gray-900">
              {courseMaterials
                ? `${courseData?.course?.title || courseData?.title || ""} • Materials`
                : "Materials"}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage all course materials, files, and resources.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {hasPermission(user, "create_material") && (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    courseMaterials && param.id
                      ? `/dashboard/courses/${param.id}/materials/create`
                      : "/dashboard/materials/create"
                  )
                }
                className="inline-flex items-center justify-center gap-2 text-white bg-primary rounded-full px-3 sm:px-4 h-10 sm:h-11 text-xs sm:text-sm font-medium hover:bg-primary-hover transition-colors"
                title="Add Material"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <svg
                    className="w-3.5 h-3.5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 18 18"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 1v16M1 9h16"
                    />
                  </svg>
                </span>
                <span className="hidden sm:inline">New Material</span>
              </button>
            )}
            {!courseMaterials && hasPermission(user, "export_material") && (
              <button
                type="button"
                onClick={exportToExcel}
                className="inline-flex items-center justify-center gap-2 text-white bg-emerald-500 hover:bg-emerald-600 rounded-full px-3 h-10 sm:h-11 text-xs sm:text-sm font-medium transition-colors"
                title="Export to Excel"
              >
                <ExcelIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setReFetchData && setReFetchData(true)}
              className="inline-flex items-center justify-center gap-2 text-gray-700 bg-white border border-gray-200 rounded-full px-3 h-10 sm:h-11 text-xs sm:text-sm font-medium hover:bg-gray-50 transition-colors"
              title="Refresh Data"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Header: Course title & Search */}
        <div className="mt-5 flex flex-col sm:flex-row justify-between items-start sm:items-center border-t border-gray-100 pt-4 gap-3">
          <span className="text-sm sm:text-base font-medium text-gray-800">
            {courseMaterials
              ? courseData?.course?.title || courseData?.title || "Course materials"
              : "All materials"}
          </span>
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search materials..."
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full rounded-full border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary transition"
            />
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M11 5a6 6 0 100 12 6 6 0 000-12z"
                />
              </svg>
            </span>
          </div>
        </div>

        {/* Table Container */}
        <div className="mt-6 rounded-2xl border border-gray-100 bg-white overflow-hidden">
          <div className="w-full overflow-x-auto">
            <div className="min-w-[960px]">
              {loader && (
                <div className="flex justify-center items-center py-6">
                  <Loader />
                </div>
              )}
              <table className="w-full table-auto">
                <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={`px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide ${
                    currentOrderBy === column.id ? "text-primary" : "text-gray-500"
                  } border-b border-gray-100 ${
                    column.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                  }`}
                  onClick={column.sortable ? () => handleSortClick(column.id) : undefined}
                >
                  <div className="flex items-center gap-1">
                    {column.label}
                    {column.sortable && renderSortIndicator(column.id)}
                  </div>
                </th>
              ))}

              {(hasPermission(user, "view_material") ||
                hasPermission(user, "delete_material")) &&
                !courseMaterials && (
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                    Actions
                  </th>
                )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {materials.length === 0 && !loader ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="px-4 sm:px-6 py-10 text-center text-gray-500"
                >
                  No materials found
                </td>
              </tr>
            ) : (
              materials.map((item, index) => (
                <tr
                  key={index}
                  className="hover:bg-gray-50 transition-colors cursor-auto text-sm"
                >
                  <td className="px-4 sm:px-6 py-4 text-gray-900 font-medium">
                    {item.course_title}
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">{item.title}</td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">{item.description}</td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">{item.media_type}</td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">
                    {formatMimeType(item.mime_type)}
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <FilePreviewCell item={item} />
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">
                    {item.uploadedBy_username}
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">{item.created_at}</td>
                  {(hasPermission(user, "view_material") ||
                    hasPermission(user, "delete_material")) &&
                    !courseMaterials && (
                      <td className="px-4 sm:px-6 py-4 text-right">
                        <ActionsDropdown actions={getDropdownActions(item.id)} />
                      </td>
                    )}
                </tr>
              ))
            )}
          </tbody>
        </table>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <p className="hidden sm:block text-xs text-gray-500">
            Showing page <span className="font-semibold">{currentPage}</span> of{" "}
            <span className="font-semibold">{totalPages}</span>
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default MaterialTable;
